import {BaseApi} from './BaseApi'

// =============================================================================
// Type definitions mirroring backend YtdImport schema and DTOs
// =============================================================================

export type YtdImportStatus =
  | 'PreviewProcessing'
  | 'PreviewReady'
  | 'NeedsResolution'
  | 'Committing'
  | 'Committed'
  | 'Failed'
  | 'Cancelled'

export interface YtdImportCoverSummary {
  client?: string
  staffingCompany?: string
  branch?: string
  bank?: string
  periodLabel?: string
  reportGeneratedAt?: string
  totalEmployees: number
  totalChecksIssued: number
  totalGrossWages: number
  totalNetPay: number
  totalEmployeeTaxes: number
  totalEmployerTaxes: number
}

export interface YtdImportEarningLine {
  systemCode: string
  paycodeName: string
  paycodeId?: string
  ytdHours: number
  ytdAmount: number
  autoCreated?: boolean
}

export interface YtdImportDeductionLine {
  code: string
  description: string
  paycodeId?: string
  isPreTax: boolean
  ytdAmount: number
  autoCreated?: boolean
}

export interface YtdImportTaxLine {
  friendlyName: string
  isEmployee: boolean
  taxId?: string
  taxType?: string
  taxDescription?: string
  ytdAmount: number
  ytdSubjectWages?: number
}

export interface YtdImportFieldDelta {
  field: string
  previousValue?: number
  newValue?: number
}

export interface YtdImportRow {
  rowIndex: number
  rawEmployeeId?: string
  rawEmployeeName?: string
  rawSsn?: string
  ssnHash?: string
  rawState?: string
  rawEmail?: string
  rawPhone?: string
  rawFirstName?: string
  rawLastName?: string
  applicantId?: string
  matchMethod: 'ssn' | 'name' | 'manual' | 'none'
  needsResolution: boolean
  skipped: boolean
  resolutionNotes?: string
  checkCount: number
  ytdGrossWages: number
  ytdTaxableWages: number
  ytdTotalEmployeeTaxes: number
  ytdTotalDeductions: number
  ytdTotalBenefits: number
  ytdNetPay: number
  employeeTaxes: YtdImportTaxLine[]
  employerTaxes: YtdImportTaxLine[]
  earnings: YtdImportEarningLine[]
  deductions: YtdImportDeductionLine[]
  hasExistingYtd: boolean
  ytdDeltas: YtdImportFieldDelta[]
  rowWarnings: string[]
  rowErrors: string[]
  lastPayDate?: string
}

export interface YtdImportAutoCreatedPaycode {
  systemCode: string
  name: string
  paycodeId?: string
  isDeduction: boolean
  reason?: string
}

/**
 * Severity for a single reconciliation row.
 *  - 'block': drift exceeding tolerance prevents confirm (unless force=true).
 *  - 'info':  drift is surfaced for visibility but never blocks confirm.
 */
export type YtdReconciliationSeverity = 'block' | 'info'

export interface YtdReconciliationLine {
  field: string
  coverValue: number
  computedValue: number
  delta: number
  withinTolerance: boolean
  /** 'block' rows over tolerance prevent confirm (unless force=true); 'info' rows never block. */
  severity: YtdReconciliationSeverity
}

/**
 * Summary of a previously-Committed YtdImport that this preview will REPLACE on confirm.
 *
 * Surfaced when the user re-uploads a file for the same (agencyId, taxYear, sourceCompanyKey)
 * slice — the UI uses this to render a "you are replacing prior import X" banner.
 */
export interface YtdPriorImportSummary {
  previewId: string
  sourceCompanyKey: string
  sourceCompanyLabel: string
  committedAt: string
  committedBy?: string
  originalFilename?: string
  /** Always true when this object is present — included for API discoverability. */
  supersedes: boolean
}

export interface YtdPreviewSummary {
  agencyId: string
  taxYear: number
  totalRows: number
  resolvedRows: number
  needsResolutionRows: number
  skippedRows: number
  rowsWithExistingYtd: number
  distinctStates: string[]
  autoCreatedPaycodeCount: number
  canConfirm: boolean
  /** Rows that WILL be committed: resolved-to-applicant, not skipped, no row-level errors. */
  eligibleEmployees: number
  /** Rows excluded because they couldn't auto-resolve and the user hasn't manually resolved them. */
  excludedNeedsResolution: number
  /** Rows excluded because the user explicitly marked them skipped. */
  excludedSkipped: number
}

/**
 * Per-bucket aggregates for the import plan. Mirrors the financial fields the user sees
 * in the preview reconciliation card so the breakdown is comparable at a glance.
 */
export interface YtdImportPlanTotals {
  employees: number
  checks: number
  ytdGrossWages: number
  ytdNetPay: number
  ytdTotalEmployeeTaxes: number
  ytdTotalEmployerTaxes: number
}

/**
 * Three-way breakdown of "what would happen if the user clicked Confirm right now".
 * Updates automatically as the user toggles skip/resolution.
 */
export interface YtdImportPlan {
  /** Rows that WILL be inserted/merged into EmployeeYTD/EmployerYTD on confirm. */
  willInsert: YtdImportPlanTotals
  /** Rows that will NOT be committed because they need resolution or have row-level errors. */
  willExcludeNeedsResolution: YtdImportPlanTotals
  /** Rows that will NOT be committed because the user explicitly skipped them. */
  willExcludeSkipped: YtdImportPlanTotals
}

export interface YtdPreviewResponse {
  previewId: string
  status: YtdImportStatus
  agencyId: string
  taxYear: number
  originalFilename: string
  fileHash: string
  /** Stable identifier for the source-company slice (cover.client normalized, or fileHash fallback). */
  sourceCompanyKey: string
  /** Display label for the source-company slice. */
  sourceCompanyLabel: string
  coverSummary?: YtdImportCoverSummary
  rows: YtdImportRow[]
  autoCreatedPaycodes: YtdImportAutoCreatedPaycode[]
  reconciliation: YtdReconciliationLine[]
  warnings: string[]
  errors: string[]
  summary: YtdPreviewSummary
  /**
   * If a prior Committed YtdImport exists for the same (agencyId, taxYear, sourceCompanyKey) slice,
   * this describes it. The UI renders a "you are replacing prior import X" banner.
   */
  priorImport?: YtdPriorImportSummary
  /**
   * Three-way breakdown driving the preview "Import Plan" panel and the confirm dialog:
   * which rows will be committed, which will be excluded for needs-resolution, which will be skipped.
   */
  plan: YtdImportPlan
}

export interface YtdRowResolution {
  rowIndex: number
  applicantId?: string
  skip?: boolean
  notes?: string
}

export interface YtdPaycodeOverride {
  systemCode: string
  paycodeId: string
}

export interface UpdateYtdResolutionsRequest {
  rowResolutions?: YtdRowResolution[]
  paycodeOverrides?: YtdPaycodeOverride[]
}

export interface ConfirmYtdImportRequest {
  forceWithReconciliationDrift?: boolean
  notes?: string
}

// =============================================================================
// API client
// =============================================================================

export class YtdImportApi extends BaseApi {
  /** POST /payroll/ytd-import/preview (multipart) */
  async createPreview(file: File, agencyId: string): Promise<YtdPreviewResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('agencyId', agencyId)
    const res = await this.client.post<YtdPreviewResponse>(
      '/payroll/ytd-import/preview',
      formData,
      {
        headers: {'Content-Type': 'multipart/form-data'},
        // Imports can take a while when discovery has to call payroll-microservice for many states.
        timeout: 120000,
      },
    )
    return res.data
  }

  /** GET /payroll/ytd-import/:previewId */
  async getPreview(previewId: string): Promise<YtdPreviewResponse> {
    const res = await this.client.get<YtdPreviewResponse>(`/payroll/ytd-import/${previewId}`)
    return res.data
  }

  /** PATCH /payroll/ytd-import/:previewId/resolutions */
  async patchResolutions(
    previewId: string,
    body: UpdateYtdResolutionsRequest,
  ): Promise<YtdPreviewResponse> {
    const res = await this.client.patch<YtdPreviewResponse>(
      `/payroll/ytd-import/${previewId}/resolutions`,
      body,
    )
    return res.data
  }

  /** POST /payroll/ytd-import/:previewId/confirm */
  async confirm(
    previewId: string,
    body: ConfirmYtdImportRequest = {},
  ): Promise<YtdPreviewResponse> {
    const res = await this.client.post<YtdPreviewResponse>(
      `/payroll/ytd-import/${previewId}/confirm`,
      body,
    )
    return res.data
  }

  /** DELETE /payroll/ytd-import/:previewId */
  async cancel(previewId: string): Promise<YtdPreviewResponse> {
    const res = await this.client.delete<YtdPreviewResponse>(`/payroll/ytd-import/${previewId}`)
    return res.data
  }

  /** GET /payroll/ytd-import/template — returns a blank YTD .xlsx template */
  async downloadTemplate(): Promise<Blob> {
    const res = await this.client.get<Blob>('/payroll/ytd-import/template', {
      responseType: 'blob',
    })
    return res.data
  }
}

export const ytdImportApi = new YtdImportApi()
